import { motion } from "framer-motion";
import {
  Layers,
  Target,
  Puzzle,
  Zap,
  Shield,
  Users,
  Handshake,
} from "lucide-react";

const reasons = [
  {
    icon: Layers,
    title: "End-to-End AI Development",
    description:
      "From ideation to deployment and beyond, we handle the complete AI lifecycle so you can focus on your business.",
  },
  {
    icon: Target,
    title: "Business-First AI Approach",
    description:
      "We don't build AI for AI's sake. Every solution is designed to solve real business problems and deliver measurable ROI.",
  },
  {
    icon: Puzzle,
    title: "Custom, Not Generic",
    description:
      "No off-the-shelf solutions. Every AI system is custom-built to fit your unique requirements and data.",
  },
  {
    icon: Zap,
    title: "Rapid Prototyping to Production",
    description:
      "Quick validation with MVPs, then seamless scaling to production-grade systems.",
  },
  {
    icon: Shield,
    title: "Security & Compliance Focused",
    description:
      "Enterprise-grade security, data privacy, and compliance built into every solution from day one.",
  },
  {
    icon: Users,
    title: "Founder-Led Consultation",
    description:
      "Direct access to our founders and senior AI engineers throughout your project journey.",
  },
  {
    icon: Handshake,
    title: "Long-Term AI Partnership",
    description:
      "We're not just vendors—we're your AI partners committed to your continued success and growth.",
  },
];

const WhyChooseUsSection = () => {
  return (
    <section
      id="why-us"
      className="py-24 md:py-32 relative"
      data-testid="why-us-section"
    >
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-5">
        <img
          src="https://images.unsplash.com/photo-1643834004015-243c2f98e490?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          {/* Left Column - Header */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-4 lg:sticky lg:top-32"
          >
            <p className="font-jetbrains text-xs tracking-widest uppercase text-neon-cyan mb-4">
              Why Choose Us
            </p>
            <h2 className="section-title mb-6">
              Your
              <br />
              <span className="text-gradient">One-Stop</span>
              <br />
              AI Partner
            </h2>
            <p className="section-subtitle">
              We combine deep technical expertise with business acumen to deliver
              AI solutions that actually work and scale.
            </p>
          </motion.div>

          {/* Right Column - Reasons Grid */}
          <div className="lg:col-span-8">
            <div className="space-y-6">
              {reasons.map((reason, index) => (
                <motion.div
                  key={reason.title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-6 flex gap-6 group hover:border-electric-violet/30"
                  data-testid={`reason-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-electric-violet/10 flex items-center justify-center flex-shrink-0 group-hover:bg-electric-violet/20 transition-colors">
                    <reason.icon className="w-6 h-6 text-electric-violet" />
                  </div>
                  <div>
                    <h3 className="font-unbounded font-medium text-lg text-white mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
